// The home page: hero, the Discover feed with its sort row, and a footer CTA.
//
// Jam is its own page (/jam), not a section here — one surface,
// reached the same way from the desktop nav and from the phone's tab bar. What
// home keeps of it is the live count, which is a reason to go there.
//
// The jam picker (/jam/start, the jam page's "Pick a seed") is this page too:
// the same feed, headed for picking, each card offering "Start Jam" where the
// remix buttons were (FeedCtaContext), and nothing that leads off elsewhere.

import { EventsBanner } from "../components/EventsBanner";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ArrowLeft } from "lucide-react";
import type { FeedTab } from "@shared/api-types.js";
import { Button } from "@web-shared/components/ui/button";
import { cn } from "@web-shared/lib/utils";
import { useAuthGate } from "../auth";
import { useSession } from "../session";
import { CardList, EmptyState } from "../components/CardList";
import { useLiveJams } from "../live";
import { UploadPicker } from "../components/UploadPicker";
import { AmbientGlow } from "@/components/AmbientGlow";
import { FeedCtaContext, type FeedCta } from "../components/feed-cta";

/** Genres are free text in Live, so this is a starting set, not an enum. */
export const GENRES = [
  "House", "Techno", "Hip Hop", "Lo-fi", "Ambient", "Drum & Bass", "Trap", "Pop",
] as const;

const SORT_TABS: Array<{ id: FeedTab; label: string }> = [
  { id: "discover", label: "Hot" },
  { id: "new", label: "New" },
  { id: "following", label: "Following" },
];

export function FeedPage({ cta = "remix" }: { cta?: FeedCta }) {
  const picking = cta === "jam";
  const session = useSession();
  const gate = useAuthGate();
  const [params, setParams] = useSearchParams();
  const [showUpload, setShowUpload] = useState(false);
  const tab = (SORT_TABS.find((t) => t.id === params.get("tab"))?.id ?? "discover") as FeedTab;
  const genre = params.get("genre") ?? "";
  const query = new URLSearchParams({ tab });
  if (genre) query.set("genre", genre);
  const liveCount = useLiveJams().length;

  const setSort = (value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== "discover") next.set("tab", value);
    else next.delete("tab");
    setParams(next, { replace: true });
  };

  const postSeed = () => setShowUpload(true);

  return (
    <FeedCtaContext.Provider value={cta}>
    <div className="bleed -mb-4">
      <AmbientGlow />

      {/* ---- What's on: a live or upcoming event, else nothing ---- */}
      {!picking && (
        <div className="relative z-10 mx-auto w-full max-w-[1300px] px-5 pt-4 sm:px-8 sm:pt-6">
          <EventsBanner />
        </div>
      )}

      {/* ---- Hero ---- */}
      <header className="relative z-10 mx-auto max-w-[1300px] w-full px-5 sm:px-8 pt-6 pb-6 sm:pb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        {picking ? (
          <div>
            <Link
              to="/jam"
              className="mb-4 inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="size-3.5" />
              Jam
            </Link>
            <h1 className="text-[40px] leading-[0.95] sm:text-5xl md:text-6xl font-black tracking-tighter sm:leading-[0.95]">
              Pick a seed to jam on.
            </h1>
            <p className="mt-3 text-sm text-muted-foreground sm:mt-4 sm:text-lg">
              Start Jam opens a live room on it. Share the link and play it together.
            </p>
          </div>
        ) : (
        <div>
          <h1 className="text-[40px] leading-[0.95] sm:text-5xl md:text-6xl font-black tracking-tighter sm:leading-[0.95]">
            Revive dead ideas.
          </h1>
          <p className="mt-3 text-sm text-muted-foreground sm:hidden">Unfinished tracks, open for remixing.</p>
          <p className="mt-4 text-lg text-muted-foreground hidden sm:block">In Ableton, Audiotool, or right here.</p>
        </div>
        )}
        <div className={cn("hidden items-center gap-3", !picking && "sm:flex")}>
          <button
            type="button"
            onClick={postSeed}
            className="flex items-center justify-center gap-2 bg-primary text-primary-foreground h-10 px-5 rounded-full font-bold text-sm hover:bg-white shadow-glow transition-all"
            title="Plant a seed"
          >
            <svg className="size-4" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
            Plant a seed
          </button>
        </div>
      </header>

      {/* ---- Sort row ---- */}
      <main id="crate" className="relative z-10 mx-auto max-w-[1300px] w-full px-5 sm:px-8 flex-1">
        {/* Mobile: pill-style filter row */}
        <div className="sm:hidden -mx-5 px-5 overflow-x-auto scrollbar-none pb-4">
          <div className="flex gap-2" role="tablist" aria-label="Sort">
            {SORT_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setSort(t.id)}
                className={cn(
                  "shrink-0 h-8 px-3.5 rounded-full text-xs font-semibold cursor-pointer transition-colors",
                  tab === t.id ? "bg-white text-black" : "border border-white/10 text-muted-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
            {!picking && (
            <Link
              to="/charts"
              className="shrink-0 h-8 px-3.5 rounded-full border border-white/10 text-xs font-semibold text-muted-foreground flex items-center"
            >
              Charts
            </Link>
            )}
            {!picking && (
            <button
              type="button"
              onClick={postSeed}
              className="shrink-0 size-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center"
              title="Plant a seed"
            >
              <svg className="size-4" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
            </button>
            )}
          </div>
        </div>
        {/* Desktop: sort row */}
        <div className="hidden sm:flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-b border-hairline pb-4">
          <div className="flex items-center gap-5" role="tablist" aria-label="Sort">
            {SORT_TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setSort(t.id)}
                className={cn(
                  "cursor-pointer text-[11px] font-bold tracking-widest uppercase transition-colors",
                  tab === t.id ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t.label}
              </button>
            ))}
            {/* Charts is not a sort of this list — it is the week, ranked, with
                the producers climbing beside it. A link, not a tab. */}
            {!picking && (
            <Link
              to="/charts"
              className="cursor-pointer text-[11px] font-bold tracking-widest uppercase text-muted-foreground transition-colors hover:text-foreground"
            >
              Charts
            </Link>
            )}
          </div>
          {liveCount > 0 && !picking && (
            <Link
              to="/jam"
              className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary hover:underline"
            >
              <span className="size-1.5 rounded-full bg-primary animate-pulse" />
              {liveCount} live now
            </Link>
          )}
        </div>

        {/* ---- Discover feed ---- */}
        <section className="pt-8 pb-20">
          {tab === "following" && !session ? (
            <EmptyState>
              <p>Follow producers to hear what they make first.</p>
              <Button size="sm" onClick={() => gate.openSignIn()}>
                Sign in
              </Button>
            </EmptyState>
          ) : (
            <CardList
              ranked={tab === "discover"}
              queryKey={["feed", tab, genre]}
              path={`/feed?${query}`}
              empty={
                <p>
                  {tab === "following"
                    ? "Nothing yet from the people you follow."
                    : "No seeds yet."}
                </p>
              }
            />
          )}
        </section>
      </main>

      {/* ---- Footer CTA — pinned to bottom ---- */}
      {/* No backdrop-blur: at 95% opaque it showed nothing, and every blur
          layer counts against iOS Safari's graphics budget. */}
      {!picking && (
      <footer className="sticky bottom-0 z-20 border-t border-hairline bg-deep/95">
        <div className="mx-auto max-w-[1300px] px-5 sm:px-8 py-2.5 flex items-center justify-between gap-4">
          <p className="text-[11px] sm:text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Got an unfinished track?</span><span className="hidden sm:inline"> Plant it as a seed.</span>
          </p>
          <button
            type="button"
            onClick={postSeed}
            className="shrink-0 bg-primary text-primary-foreground px-3 py-1 sm:px-4 sm:py-1.5 rounded-full text-[11px] sm:text-sm font-semibold hover:bg-white shadow-glow transition-all"
          >
            Plant a seed
          </button>
        </div>
      </footer>
      )}

      {showUpload && <UploadPicker onClose={() => setShowUpload(false)} />}
    </div>
    </FeedCtaContext.Provider>
  );
}
